import React, { useState, useMemo } from 'react'
import './account-detail.scss'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import { Column, Row } from '@/components/layout/layout'
import { useRequest, useLocalStorageState } from 'ahooks'
import { $post } from '@/api'
import { IAccountChecking } from '../account-chart/type'
import { ColumnsType } from 'antd/lib/table'
import { useAccountCompute } from '../account-checking'
import { IUser } from '@/model/types/user'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Table } from 'antd'
import FormTitle from '../../../goods-manage/components/form/form-title/form-title'
import StaffDetail from '../../../staff-manage/staff-list/staff-detail/staff-detail'
import { formatPrice } from '@/utils'

interface AccountDetailProps {
    date: [string, string]
}

type DetailRecord = Record<
    string,
    {
        name: string
        id: number
        amount: number
    }
> & {
    cashiers: {
        id: number
        user: IUser
    }
}

interface SumData {
    id: number
    amount: number
    name: string
}

const AccountDetail: React.FC<AccountDetailProps> = props => {
    const [sortData] = useLocalStorageState<ColumnsType<DetailRecord>>(
        `ACCOUNT_SORT_COLUMN_${localStorage.getItem('shopId')}`,
        [],
    )
    const [detailData, setDetailData] = useState<DetailRecord[]>([])
    const [sumData, setSumData] = useState<SumData[]>([])
    const { getPaymentData } = useAccountCompute()
    const cls = useBemClass('account-detail')

    const accountDetailReq = useRequest<IAccountChecking.AccountDetail>(
        () =>
            $post({
                url: '/finance/getcashierfundstatistics',
                params: {
                    from_date: props.date[0],
                    to_date: props.date[1],
                },
            }),
        {
            onSuccess: data => {
                const { customPaymentData, totalData, paymentData } = getPaymentData(data.sums)

                setSumData(() =>
                    [totalData, ...paymentData, ...customPaymentData].map(p => ({
                        id: p.id,
                        amount: p.amount,
                        name: p.name,
                    })),
                )

                setDetailData(
                    data.data_list.map(d => {
                        const { customPaymentData, totalData, paymentData } = getPaymentData(d)

                        const payment_data = [totalData, ...paymentData, ...customPaymentData]
                        const payment_table_data = payment_data.reduce((pre, next) => {
                            pre[`payment:${next.id}`] = next

                            return pre
                        }, {} as Record<string, any>)

                        return {
                            cashiers: d.cashier_staff,
                            ...payment_table_data,
                        } as DetailRecord
                    }),
                )
            },
        },
    )

    const columns: ColumnsType<DetailRecord> = useMemo(() => {
        return [
            {
                title: '#',
                width: 70,
                render: (r, v, i) => i + 1,
            },
            {
                title: '收银员',
                width: 140,
                render: (val, record) => {
                    return (
                        <Row data-align-items-center>
                            <img
                                style={{
                                    border: `1px solid ${record.cashiers.user.gender === 2 ? '#ee5b5b' : '#3c90ff'}`,
                                }}
                                className={cls('avatar')}
                                src={record.cashiers.user.avatar}
                                alt=""
                            />
                            <div
                                className={`${cls('nickname')} action`}
                                onClick={() => {
                                    showLayer(<StaffDetail staffId={record.cashiers.id} />)
                                }}
                            >
                                {record.cashiers.user.realname}
                            </div>
                        </Row>
                    )
                },
            },
            ...sortData
                // @ts-ignore
                ?.filter(s => (typeof s.checked === undefined ? true : s.checked))
                .map(s => ({
                    ...s,
                    render: (val: any) => formatPrice(val?.amount || 0),
                    // @ts-ignore
                    sorter: (a, b) => a[s.dataIndex].amount - b[s.dataIndex].amount,
                })),
        ]
    }, [cls, sortData])

    return (
        <BaseDrawer width={700} isShow onClose={() => layerManage.pop()} title="收银台资金详情">
            <Column>
                <div data-color-999 data-font-size-16 data-margin-top-15 data-margin-left-16>
                    时间：{props.date[0]}至{props.date[1]}
                </div>
                <Row data-aling-items-center className={cls('container')}>
                    {sumData.map(s => (
                        <div key={s.id} className={cls('item')}>
                            <div data-color-666>{s.name}</div>
                            <div data-color-333>¥{s.amount}</div>
                        </div>
                    ))}
                </Row>
                <FormTitle hasLine={false} title="收银员收款详情" />
                <div className="table-container">
                    <Table
                        rowKey={record => record.cashiers.id}
                        bordered
                        loading={accountDetailReq.loading}
                        pagination={false}
                        columns={columns}
                        dataSource={detailData}
                        scroll={{ x: (sortData?.length || 9) * 130 + 300 }}
                    />
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default AccountDetail
